import { Typography } from "@mui/material";
import { Fragment } from "react";
import { useSelector } from "react-redux";
import { frequencyEnum, mealTypesEnum } from "../../../util/enum";
import { getRightPackageUom } from "../../../util/function";

const COL_COUNT = 7;

const PrescriptionView = ({ prescriptions, showShortName = false }) => {
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const { clinicStocks } = useSelector((state) => state?.clinic);

  const getBatchList = (stockId = null) => {
    if (!stockId) return [];
    const reqStock = clinicStocks?.data?.find(
      (stc) => stc?.stockId === stockId,
    );
    return reqStock?.stockBatch ?? [];
  };

  const getNameWithValue = (itm) => {
    if (itm?.shortName?.length > 0) return itm?.shortName;
    if (itm?.name?.length > 0) return itm?.name;
    if (itm?.plName?.length > 0) return itm?.plName;
    return "";
  };

  const getFrequencyLabel = (li) => {
    if (Array.isArray(li?.frequencyDuration)) {
      return li?.frequencyDuration
        ?.map((key) => frequencyEnum[key])
        ?.filter(Boolean)
        ?.join(", ");
    }
    if (typeof li?.frequencyDuration === "string") {
      return li?.frequencyDuration;
    }
    return `${li?.morning ? "1" : "0"}-${li?.afternoon ? "1" : "0"}-${
      li?.night ? "1" : "0"
    }`;
  };

  const getDoseLabel = (li) => {
    const packageUom = li?.packageUom
      ? (getBatchList(li?.stockId)?.find((bat) => bat?.batchId === li?.batchId)
          ?.packageUom ?? "")
      : (getBatchList(li?.stockId)?.[0]?.packageUom ?? "");
    return `${li?.dosage ?? ""}${getRightPackageUom(packageUom, true) ?? ""}`;
  };

  const prescriptionList = prescriptions ?? reqDet?.prescription ?? [];
  const areAllEmpty = prescriptionList.every(
    (pres) => ((pres?.prescriptionDetails ?? [])?.length ?? 0) === 0,
  );

  if (areAllEmpty) {
    return <div className="prescription-print-table" />;
  }

  return (
    <div className="prescription-print-table">
      <table className="prescription-print-native-table">
        <colgroup>
          <col className="prescription-print-col-name" />
          <col className="prescription-print-col-dose" />
          <col className="prescription-print-col-freq" />
          <col className="prescription-print-col-route" />
          <col className="prescription-print-col-duration" />
          <col className="prescription-print-col-instructions" />
          <col className="prescription-print-col-remarks" />
        </colgroup>
        <thead>
          <tr>
            <th>
              <Typography className="fs12 txt-semi-bold black">Name</Typography>
            </th>
            <th>
              <Typography className="fs12 txt-semi-bold black">Dose</Typography>
            </th>
            <th>
              <Typography className="fs12 txt-semi-bold black">
                Frequency
              </Typography>
            </th>
            <th>
              <Typography className="fs12 txt-semi-bold black">Route</Typography>
            </th>
            <th>
              <Typography className="fs12 txt-semi-bold black">
                Duration
              </Typography>
            </th>
            <th>
              <Typography className="fs12 txt-semi-bold black">
                Instructions
              </Typography>
            </th>
            <th>
              <Typography className="fs12 txt-semi-bold black">
                Remarks
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {prescriptionList.map((pres, i) => {
            const reqProcedDetList = [
              ...(pres?.prescriptionDetails ?? []),
            ]?.sort(
              (a, b) => (a?.id ?? a?.detailId) - (b?.id ?? b?.detailId),
            );
            const notes = pres?.remark || pres?.remarks || "";

            return (
              <Fragment key={`pres_group_${i}`}>
                {reqProcedDetList?.map((li, rowIdx) => (
                  <tr key={`rec_${li?.id ?? li?.i ?? `${i}_${rowIdx}`}`}>
                    <td>
                      <Typography className="fs12 font-medium black4">
                        {showShortName
                          ? getNameWithValue(li)
                          : (li?.name ?? li?.plName)}
                      </Typography>
                    </td>
                    <td>
                      <Typography className="fs12 font-medium black4">
                        {getDoseLabel(li)}
                      </Typography>
                    </td>
                    <td>
                      <div className="fs12 font-medium black4">
                        {getFrequencyLabel(li)}
                      </div>
                    </td>
                    <td>
                      <Typography className="fs12 font-medium black4">
                        {li?.route ?? ""}
                      </Typography>
                    </td>
                    <td>
                      <Typography className="fs12 font-medium black4">
                        {li?.duration ?? "1"} days
                      </Typography>
                    </td>
                    <td>
                      <div className="fs12 font-medium black4">
                        {mealTypesEnum?.[li?.meal ?? ""]}
                      </div>
                    </td>
                    <td>
                      <div className="fs12 font-medium black4">
                        {li?.remark ?? ""}
                      </div>
                    </td>
                  </tr>
                ))}
                {notes?.length > 0 ? (
                  <tr className="prescription-print-notes-row">
                    <td className="prescription-print-notes" colSpan={COL_COUNT}>
                      <Typography className="fs12 txt-semi-bold black mt10">
                        Notes
                      </Typography>
                      <Typography
                        className="fs12 txt-regular black"
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                        component="div"
                      >
                        {notes?.split("\n")?.map((line, index) => (
                          <div key={index}>{line || "\u00A0"}</div>
                        ))}
                      </Typography>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PrescriptionView;
